/**
 * Patient Document Center - Enhanced with TreatX Platform Documents
 * Page for patients to view, sign, and manage their legal documents
 * Includes:
 * - TreatX Platform Documents (GDPR, Payment Policy) with blocking modals
 * - Clinic-Specific Documents with stage requirements
 */
import React, { useState, useEffect } from 'react';
import { formatDateLocale, formatDateTimeLocale } from '../../utils/dateFormat';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDocuments, REQUIRED_STAGES } from '../../context/DocumentContext';
import { usePatientFlow } from '../../context/PatientFlowContext';
import { useAuth } from '../../context/AuthContext';
import ESignatureModal from '../../components/ESignatureModal';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  FileText, CheckCircle, Clock, AlertTriangle, ArrowLeft,
  Download, Eye, PenTool, Shield, FileCheck, Inbox, Archive,
  Heart, Building2, Lock, X, ChevronRight
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../../components/Toaster';
import { useContent } from '../../hooks/useContent';
import { SafeHtml } from '../../components/security/SafeHtml';

// Helper function for category icons
const getCategoryIcon = (category) => {
  switch (category) {
    case 'consent':
      return <Shield className="h-5 w-5 text-blue-600" />;
    case 'medical':
      return <FileCheck className="h-5 w-5 text-green-600" />;
    case 'legal':
      return <FileText className="h-5 w-5 text-purple-600" />;
    case 'financial':
      return <FileText className="h-5 w-5 text-amber-600" />;
    default:
      return <FileText className="h-5 w-5 text-slate-600" />;
  }
};

// Blocking Modal Component for required TreatX documents
const BlockingDocumentModal = ({ document, onSign, onClose, patientName }) => {
  const { t } = useTranslation();
  const [agreed, setAgreed] = useState(false);
  const [typedName, setTypedName] = useState('');
  const [loading, setLoading] = useState(false);

  // CMS content for document summaries and acceptance text
  const docSummaryKey = document?.id === 'treatx-doc-001' ? 'gdpr_doc_summary' : document?.id === 'treatx-doc-002' ? 'payment_policy_doc_summary' : null;
  const { content: summaryContent } = useContent(docSummaryKey, { flow: 'document_signing' });
  const { content: acceptanceContent } = useContent('document_acceptance_text', { flow: 'document_signing' });

  const handleSign = async () => {
    if (!agreed || !typedName.trim()) return;
    
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    onSign({ agreed, typedName: typedName.trim() });
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-6 sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Heart className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">{t('patient.documents.platformCardTitle')}</h2>
              <p className="text-emerald-100 text-sm">{t('patient.documents.cardHeaderRequired')}</p>
            </div>
          </div>
        </div>

        {/* Alert Banner */}
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-3">
          <div className="flex items-center gap-2 text-amber-800">
            <Lock className="h-5 w-5" />
            <p className="font-medium">{document.blockingMessage}</p>
          </div>
        </div>

        {/* Document Content */}
        <div className="p-6">
          <h3 className="text-lg font-bold text-slate-800 mb-2">{document.title}</h3>
          <p className="text-slate-600 mb-4">{document.description}</p>
          
          {/* Document Preview */}
          <div className="bg-slate-50 border rounded-lg p-4 text-sm text-slate-600 space-y-3">
            <p><strong>Version:</strong> {document.currentVersion}</p>
            <p><strong>{t('patient.documents.signedOn').split(':')[0]}:</strong> {document.versions?.[0]?.uploadDate}</p>
            
            <div className="border-t pt-3 mt-3">
              <p className="font-medium text-slate-700 mb-2">{t('patient.offers.packageDetails')}:</p>
              {summaryContent ? (
                <SafeHtml html={summaryContent.body} className="text-xs" />
              ) : (
                <p className="text-xs">{t('patient.documents.reviewBeforeSign')}</p>
              )}
            </div>
          </div>
        </div>

        {/* Signature Section - Sticky at bottom for visibility */}
        <div className="border-t bg-slate-50 p-6 space-y-4 sticky bottom-0">
          {/* Agreement Checkbox */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
            />
            <span className="text-sm text-slate-700">
              {acceptanceContent?.body || 'I have read and agree to the terms above.'}{' '}
              <strong>{document.title}</strong>
            </span>
          </label>

          {/* Typed Signature */}
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">
              Type your full name to sign
            </label>
            <input
              type="text"
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              placeholder={patientName}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              onClick={handleSign}
              disabled={!agreed || !typedName.trim() || loading}
              className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
            >
              {loading ? t('patient.common.loading') : (
                <>
                  <PenTool className="h-4 w-4 mr-2" />
                  {t('patient.documents.signAndAccept')}
                </>
              )}
            </Button>
            <Button variant="outline" onClick={onClose}>
              {t('patient.documents.reviewLater')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Document Card Component - Enhanced with owner indicator
const DocumentCard = ({ document, type = 'pending', onViewAndSign, onDownload, isTreatx = false }) => {
  const { t } = useTranslation();
  const isSigned = type === 'signed';
  const isOptional = type === 'optional';
  const stage = REQUIRED_STAGES[document.requiredStage];
  
  // Priority 1 Item 1: Handle document download
  const handleDownload = () => {
    const docContent = `
${document.title}
${'='.repeat(document.title.length)}

Version: ${document.currentVersion}
Category: ${document.category}
Status: ${isSigned ? 'SIGNED' : 'PENDING'}
${isSigned && document.signature ? `Signed on: ${formatDateTimeLocale(document.signature.signedAt)}` : ''}

${document.description}

---
Document ID: ${document.id}
Generated by TreatX Health Platform
© ${new Date().getFullYear()} TreatX Health. All rights reserved.
    `;
    
    const blob = new Blob([docContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = window.document.createElement('a');
    link.href = url;
    link.download = `${document.title.replace(/\s+/g, '_')}_v${document.currentVersion}.txt`;
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };
  
  return (
    <Card className={`transition-all hover:shadow-lg ${
      isSigned ? 'bg-green-50/50 border-green-200' : 
      isOptional ? 'bg-slate-50/50' : 
      isTreatx ? 'bg-emerald-50/30 border-emerald-200' : ''
    }`}>
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
            isSigned ? 'bg-green-100' :
            isOptional ? 'bg-slate-100' :
            isTreatx ? 'bg-emerald-100' : 'bg-blue-100'
          }`}>
            {isSigned ? (
              <CheckCircle className="h-6 w-6 text-green-600" />
            ) : isTreatx ? (
              <Heart className="h-6 w-6 text-emerald-600" />
            ) : (
              getCategoryIcon(document.category)
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-slate-900">{document.title}</h3>
                  {isTreatx && (
                    <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                      <Heart className="h-3 w-3 mr-1" />
                      TreatX
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-slate-600 mt-1 line-clamp-2">
                  {document.description}
                </p>
              </div>
              
              <div className="flex flex-col items-end gap-2">
                {document.isRequired && !isSigned && (
                  <Badge className="bg-red-100 text-red-700 border-red-200">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    {t('patient.documents.requiredBadge')}
                  </Badge>
                )}
                {isSigned && (
                  <Badge className="bg-green-100 text-green-700 border-green-200">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    {t('patient.documents.signedBadge')}
                  </Badge>
                )}
                {isOptional && (
                  <Badge variant="outline" className="text-slate-500">
                    {t('patient.documents.optionalBadge')}
                  </Badge>
                )}
                {stage && !isSigned && (
                  <Badge className={`text-xs ${stage.color}`}>
                    {stage.label}
                  </Badge>
                )}
              </div>
            </div>

            {/* Meta Info */}
            <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
              <span>Version {document.currentVersion}</span>
              <span>•</span>
              <span>Updated {document.versions?.[0]?.uploadDate}</span>
              {isSigned && document.signature && (
                <>
                  <span>•</span>
                  <span>Signed {formatDateLocale(document.signature.signedAt)}</span>
                </>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 mt-4">
              {!isSigned && document.requiresSignature && (
                <Button 
                  onClick={() => onViewAndSign(document, isTreatx)}
                  className={isTreatx 
                    ? 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                  }
                >
                  <PenTool className="h-4 w-4 mr-2" />
                  Review & Sign
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => onViewAndSign(document, isTreatx)}>
                <Eye className="h-4 w-4 mr-2" />
                Preview
              </Button>
              <Button variant="ghost" size="sm" onClick={handleDownload}>
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const PatientDocuments = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { flowState } = usePatientFlow();
  const { user } = useAuth();
  const {
    getTreatxDocuments,
    getPendingTreatxDocuments,
    getPendingClinicDocuments,
    getSignedDocuments,
    getOptionalDocuments,
    signDocument,
    isGdprSigned,
    isPaymentPolicySigned,
    getBlockingDocument
  } = useDocuments();

  // Patient data from auth context
  const patientId = user?.id || user?.patient_id || '';
  const patientName = user?.name || 'Patient';
  const clinicId = flowState.clinicsSelected?.[0] || '';

  const [signModalOpen, setSignModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedIsTreatx, setSelectedIsTreatx] = useState(false);
  const [activeTab, setActiveTab] = useState('treatx');
  const [blockingModal, setBlockingModal] = useState(null);

  // Get documents
  const treatxDocuments = getTreatxDocuments();
  const pendingTreatxDocuments = getPendingTreatxDocuments(patientId);
  const pendingClinicDocuments = getPendingClinicDocuments(patientId, clinicId);
  const signedDocuments = getSignedDocuments(patientId, clinicId);
  const optionalDocuments = getOptionalDocuments(patientId, clinicId);

  // Check for blocking documents on mount only - using useRef to track if mounted
  const hasCheckedBlocking = React.useRef(false);
  
  useEffect(() => {
    if (hasCheckedBlocking.current) return;
    hasCheckedBlocking.current = true;
    
    // Check for PRE_INTAKE blocking document (GDPR)
    const gdprSigned = isGdprSigned(patientId);
    if (!gdprSigned) {
      const docs = getTreatxDocuments();
      const blockingDoc = docs.find(
        d => d.id === 'treatx-doc-001' && d.isBlocking
      );
      if (blockingDoc) {
        // Use setTimeout to avoid setState during render
        setTimeout(() => setBlockingModal(blockingDoc), 0);
      }
    }
  }, [isGdprSigned, getTreatxDocuments, patientId]);

  const handleViewAndSign = (document, isTreatx = false) => {
    setSelectedDocument(document);
    setSelectedIsTreatx(isTreatx);
    setSignModalOpen(true);
  };

  const handleSign = async (signatureData) => {
    await signDocument(patientId, selectedDocument.id, clinicId, signatureData);
    
    toast({
      title: 'Document Signed',
      description: `"${selectedDocument.title}" has been signed successfully.`
    });
  };

  const handleBlockingSign = async (signatureData) => {
    await signDocument(patientId, blockingModal.id, clinicId, signatureData);
    setBlockingModal(null);
    
    toast({
      title: 'Document Signed',
      description: `"${blockingModal.title}" has been signed. You can now proceed.`
    });
  };

  // Calculate totals for stats
  const totalPending = pendingTreatxDocuments.length + pendingClinicDocuments.length;
  const totalTreatx = treatxDocuments.length;
  const treatxSigned = treatxDocuments.length - pendingTreatxDocuments.length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <Toaster />
      
      {/* Blocking Modal */}
      {blockingModal && (
        <BlockingDocumentModal
          document={blockingModal}
          onSign={handleBlockingSign}
          onClose={() => setBlockingModal(null)}
          patientName={patientName}
        />
      )}
      
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="text-slate-300 hover:text-white mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center">
              <FileText className="h-7 w-7" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">{t('patient.documents.title')}</h1>
              <p className="text-slate-300 mt-1">
                {t('patient.documents.docCenterSubtitle')}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Heart className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-700">{treatxSigned}/{totalTreatx}</p>
                <p className="text-sm text-emerald-600">{t('patient.documents.counter_treatx')}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <Clock className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-700">{totalPending}</p>
                <p className="text-sm text-amber-600">{t('patient.documents.counter_pending')}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-700">{signedDocuments.length}</p>
                <p className="text-sm text-green-600">{t('patient.documents.counter_signed')}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-slate-50 to-gray-50 border-slate-200">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                <Archive className="h-6 w-6 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-700">{optionalDocuments.length}</p>
                <p className="text-sm text-slate-600">{t('patient.documents.counter_optional')}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Urgent Alert */}
        {totalPending > 0 && (
          <Card className="mb-6 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-300">
            <CardContent className="p-4 flex items-center gap-4">
              <AlertTriangle className="h-6 w-6 text-amber-600" />
              <div className="flex-1">
                <p className="font-medium text-amber-800">
                  You have {totalPending} document{totalPending !== 1 ? 's' : ''} requiring your signature
                </p>
                <p className="text-sm text-amber-700">
                  {pendingTreatxDocuments.length > 0 && `${pendingTreatxDocuments.length} TreatX platform document${pendingTreatxDocuments.length !== 1 ? 's' : ''}`}
                  {pendingTreatxDocuments.length > 0 && pendingClinicDocuments.length > 0 && ' and '}
                  {pendingClinicDocuments.length > 0 && `${pendingClinicDocuments.length} clinic document${pendingClinicDocuments.length !== 1 ? 's' : ''}`}
                </p>
              </div>
              <Button 
                onClick={() => setActiveTab('treatx')}
                className="bg-amber-600 hover:bg-amber-700"
              >
                Sign Now
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Success Banner - All Required Documents Signed */}
        {totalPending === 0 && signedDocuments.length > 0 && (
          <Card className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 border-green-300">
            <CardContent className="p-4 flex items-center gap-4">
              <CheckCircle className="h-6 w-6 text-green-600" />
              <div className="flex-1">
                <p className="font-medium text-green-800">
                  All required documents signed!
                </p>
                <p className="text-sm text-green-700">
                  You&apos;re ready to proceed to the next step in your medical journey.
                </p>
              </div>
              <Button 
                onClick={() => navigate('/patient/offers')}
                className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
              >
                Continue to Offers
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Document Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="treatx" className="relative">
              <Heart className="h-4 w-4 mr-2" />
              {t('patient.documents.tab_platform')}
              {pendingTreatxDocuments.length > 0 && (
                <Badge className="ml-2 bg-emerald-500 text-white">{pendingTreatxDocuments.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="clinic" className="relative">
              <Building2 className="h-4 w-4 mr-2" />
              {t('patient.documents.tab_clinic')}
              {pendingClinicDocuments.length > 0 && (
                <Badge className="ml-2 bg-red-500 text-white">{pendingClinicDocuments.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="signed">
              <CheckCircle className="h-4 w-4 mr-2" />
              {t('patient.documents.tab_signed')}
            </TabsTrigger>
            <TabsTrigger value="optional">
              <Archive className="h-4 w-4 mr-2" />
              {t('patient.documents.tab_optional')}
            </TabsTrigger>
          </TabsList>

          {/* TreatX Platform Documents */}
          <TabsContent value="treatx" className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2 text-emerald-800">
                <Heart className="h-5 w-5" />
                <p className="font-medium">{t('patient.documents.platformCardTitle')}</p>
              </div>
              <p className="text-sm text-emerald-700 mt-1">
                {t('patient.documents.platformCardBody')}
              </p>
            </div>
            
            {treatxDocuments.map(doc => {
              const isSigned = !pendingTreatxDocuments.find(d => d.id === doc.id);
              return (
                <DocumentCard 
                  key={doc.id} 
                  document={isSigned ? { ...doc, signature: { signedAt: new Date().toISOString() } } : doc}
                  type={isSigned ? 'signed' : 'pending'}
                  isTreatx={true}
                  onViewAndSign={(doc) => handleViewAndSign(doc, true)}
                />
              );
            })}
          </TabsContent>

          {/* Clinic Documents */}
          <TabsContent value="clinic" className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2 text-blue-800">
                <Building2 className="h-5 w-5" />
                <p className="font-medium">{t('patient.documents.clinicCardTitle')}</p>
              </div>
              <p className="text-sm text-blue-700 mt-1">
                These documents are specific to your selected clinic and treatment.
              </p>
            </div>
            
            {pendingClinicDocuments.length > 0 ? (
              pendingClinicDocuments.map(doc => (
                <DocumentCard 
                  key={doc.id} 
                  document={doc} 
                  type="pending" 
                  onViewAndSign={handleViewAndSign}
                />
              ))
            ) : (
              <Card className="py-12">
                <CardContent className="text-center">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-800">{t('patient.documents.clinicEmpty')}</h3>
                  <p className="text-slate-600">{t('patient.documents.clinicEmpty')}</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Signed Documents */}
          <TabsContent value="signed" className="space-y-4">
            {signedDocuments.length > 0 ? (
              signedDocuments.map(doc => (
                <DocumentCard 
                  key={doc.id} 
                  document={doc} 
                  type="signed"
                  isTreatx={doc.owner === 'TREATX'}
                  onViewAndSign={handleViewAndSign}
                />
              ))
            ) : (
              <Card className="py-12">
                <CardContent className="text-center">
                  <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-800">{t('patient.documents.empty')}</h3>
                  <p className="text-slate-600">{t('patient.documents.empty')}</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Optional Documents */}
          <TabsContent value="optional" className="space-y-4">
            {optionalDocuments.length > 0 ? (
              optionalDocuments.map(doc => (
                <DocumentCard 
                  key={doc.id} 
                  document={doc} 
                  type="optional" 
                  onViewAndSign={handleViewAndSign}
                />
              ))
            ) : (
              <Card className="py-12">
                <CardContent className="text-center">
                  <Archive className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-800">{t('patient.documents.empty')}</h3>
                  <p className="text-slate-600">{t('patient.documents.optionalEmpty')}</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* E-Signature Modal for regular documents */}
      <ESignatureModal
        isOpen={signModalOpen}
        onClose={() => {
          setSignModalOpen(false);
          setSelectedDocument(null);
        }}
        document={selectedDocument}
        onSign={handleSign}
        patientName={patientName}
      />
    </div>
  );
};

export default PatientDocuments;
